import { Coins } from 'lucide-react';
import { formatRunCost, formatRunTokens, runUsageTokens, type RunUsage } from '@repo/shared-types';
import { cn } from '../../lib/utils';

/**
 * What a run spent, wherever a run is shown.
 *
 * One component for both kinds of run, like everything else in this folder: an ingestion run and an
 * automation run carry the same six usage columns, and a cost that renders differently depending on
 * which agent produced it would read as two features.
 *
 * The number is only ever shown when there is one. A run from before usage was recorded, a run still
 * sitting in the queue and a provider that reports no price all leave `costUsd` at zero, and `$0`
 * beside a run that plainly did work is worse than nothing there — so the chip hides itself and the
 * tokens carry the story instead.
 */

/** The flat usage columns as every run row carries them. */
export interface RunUsageRow {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  modelCalls: number;
}

export function runUsageOf(row: RunUsageRow): RunUsage {
  return {
    costUsd: row.costUsd,
    inputTokens: row.inputTokens,
    outputTokens: row.outputTokens,
    cachedTokens: row.cachedTokens,
    reasoningTokens: row.reasoningTokens,
    modelCalls: row.modelCalls,
  };
}

/**
 * Everything the row knows, as one sentence — the hover text behind every cost shown in the app.
 *
 * Cached and reasoning tokens are named only when there are any: they are the two figures that
 * explain a surprising bill (a cheap run that re-read a huge prompt from cache, an expensive one that
 * spent its output budget thinking), and listing them at zero would bury that under noise.
 */
export function describeRunUsage(usage: RunUsage): string {
  const parts = [
    `${formatRunTokens(usage.inputTokens)} in · ${formatRunTokens(usage.outputTokens)} out`,
    usage.cachedTokens > 0 ? `${formatRunTokens(usage.cachedTokens)} cached` : '',
    usage.reasoningTokens > 0 ? `${formatRunTokens(usage.reasoningTokens)} reasoning` : '',
    `${usage.modelCalls} model call${usage.modelCalls === 1 ? '' : 's'}`,
  ].filter(Boolean);
  return `${formatRunCost(usage.costUsd)} — ${parts.join(' · ')}`;
}

/** Compact enough for a list row: the cost, with the whole breakdown on hover. */
export function RunCostChip({ usage, className }: { usage: RunUsage; className?: string }) {
  if (usage.costUsd <= 0) return null;

  return (
    <span
      title={describeRunUsage(usage)}
      className={cn('text-[10px] text-ink-faint inline-flex items-center gap-1 tabular-nums', className)}
    >
      <Coins size={9} />
      {formatRunCost(usage.costUsd)}
    </span>
  );
}

/**
 * The cost as a pair of stats, for a run's detail panel.
 *
 * Tokens sit beside the money on purpose. Cost is the number anyone asks for first and the one that
 * ages worst — prices change, models get swapped — while the token counts are what the run actually
 * did, and they are what makes two runs comparable a year apart.
 */
export function RunCostStats({ usage }: { usage: RunUsage }) {
  const tokens = runUsageTokens(usage);

  return (
    <>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-ink-faint">Cost</p>
        <p className="text-ink tabular-nums" title={describeRunUsage(usage)}>
          {usage.modelCalls > 0 ? formatRunCost(usage.costUsd) : '—'}
        </p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-ink-faint">Tokens</p>
        <p className="text-ink tabular-nums" title={describeRunUsage(usage)}>
          {tokens > 0 ? formatRunTokens(tokens) : '—'}
        </p>
      </div>
    </>
  );
}
